import type { InspectionTemplateBody } from '@ayumia/contracts';
import { text } from '../../app/i18n';
import type { ScopeVariable } from '../editor/condition-model';
import type { BlockKind, ChangeClass, EditorNode, Finding } from '../editor/editor-types';
import { formatPointer, getAt, parsePointer, type Pointer } from '../editor/json-pointer';

/**
 * Profil « formulaire » du socle d'éditeur (ADR-141 §2) : comment un gabarit `inspection-template-body`
 * 1.3.0 se lit en arbre de blocs (formulaire › sections › champs, sous-lignes d'une section répétée),
 * quels blocs la palette ajoute, quelles variables une condition peut citer, et les contrôles locaux.
 * Pur : aucune interface, aucun appel. Le serveur reste l'autorité (`ValidateInspectionTemplateDraft`).
 */

export type Template = InspectionTemplateBody;
type Section = Template['sections'][number];
type Entry = Section['fields'][number];

const ID_PATTERN = /^[a-z][a-z0-9_]*$/;
const isRecord = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === 'object' && !Array.isArray(value);

export const TYPE_LABELS: Readonly<Record<string, string>> = {
  string: 'Texte',
  rich_text: 'Texte riche',
  number: 'Nombre',
  decimal: 'Décimal',
  quantity: 'Quantité',
  money: 'Montant',
  boolean: 'Oui / non',
  date: 'Date',
  datetime: 'Date et heure',
  duration: 'Durée',
  enumeration: 'Liste de valeurs',
  reference: 'Objet à choisir',
  collection: 'Collection',
  file: 'Fichier',
  json: 'Structure',
};

export const TYPE_GLYPHS: Readonly<Record<string, string>> = { string: 'Aa', rich_text: 'Aa', number: '1,0', decimal: '1,0', quantity: '1,0', money: '€', boolean: '✓', date: 'JJ', datetime: 'JJ', duration: 'h', enumeration: '≡', reference: '→', collection: '[ ]', file: '⎙', json: '{ }' };

/** Icône du design system par type de champ (ADR-146 addendum (15)), à la place des glyphes dans le studio. */
export const TYPE_ICONS: Readonly<Record<string, string>> = { string: 'text', rich_text: 'text', number: 'hash', decimal: 'hash', quantity: 'hash', money: 'euro', boolean: 'toggle', date: 'calendar', datetime: 'calendar', duration: 'clock', enumeration: 'list', reference: 'link', collection: 'rows', file: 'file', json: 'database' };

/** Icône d'une entrée de la palette des champs. */
const KIND_ICONS: Readonly<Record<string, string>> = { string: 'text', number: 'hash', measure: 'thermometer', boolean: 'toggle', enumeration: 'list', date: 'calendar', datetime: 'clock', reference: 'link', photo: 'image', signature: 'signature' };

/** Identifiant libre dans le formulaire (`champ_3`) : les réponses sont à plat, un identifiant n'est jamais réutilisé. */
export function freshId(template: Template, prefix: string): string {
  const used = new Set<string>();
  for (const section of template.sections) {
    used.add(section.section_id);
    for (const entry of section.fields) used.add(entry.field.field_id);
    for (const entry of section.repeat?.children?.fields ?? []) used.add(entry.field.field_id);
  }
  let index = 1;
  while (used.has(`${prefix}_${index}`)) index += 1;
  return `${prefix}_${index}`;
}

function fieldKind(id: string, label: string, type: string, extra: (template: Template, fieldId: string) => Partial<Entry> = () => ({})): BlockKind<Template> {
  return {
    id,
    label,
    glyph: TYPE_GLYPHS[type] ?? '?',
    create: (template) => {
      const fieldId = freshId(template, 'champ');
      const base: Entry = { field: { field_id: fieldId, label: { fr: label }, type: type as Entry['field']['type'] } };
      const more = extra(template, fieldId);
      return { ...base, ...more, field: { ...base.field, ...(more.field ?? {}) } };
    },
  };
}

/** Palette des champs : une case typée d'après une table type → modèle par défaut (ADR-141 §2.1). */
export const FIELD_KINDS: readonly BlockKind<Template>[] = [
  fieldKind('string', 'Texte', 'string'),
  fieldKind('number', 'Nombre', 'number', () => ({ presentation: { widget: 'keypad' } })),
  fieldKind('measure', 'Mesure à cible', 'number', () => ({ field: { unit: '°C' } as unknown as Entry['field'], presentation: { widget: 'keypad' }, target: { min: 0, max: 4, unit: '°C', on_out_of_target: 'nc_prompt' } })),
  fieldKind('boolean', 'Oui / non', 'boolean', () => ({ presentation: { widget: 'toggle' } })),
  fieldKind('enumeration', 'Liste de valeurs', 'enumeration', () => ({ field: { validation: { enum_values: ['conforme', 'non_conforme'], enum_labels: { conforme: { fr: 'Conforme' }, non_conforme: { fr: 'Non conforme' } } } } as unknown as Entry['field'], presentation: { widget: 'radio_tiles' }, nonconformity_when: { $eq: ['$answers.__self__', 'non_conforme'] } })),
  fieldKind('date', 'Date', 'date', () => ({ presentation: { widget: 'date' } })),
  fieldKind('datetime', 'Date et heure', 'datetime', () => ({ presentation: { widget: 'datetime' } })),
  fieldKind('reference', 'Objet à choisir', 'reference', () => ({ field: { reference_type: 'Location' } as unknown as Entry['field'], presentation: { widget: 'selector', selector: { label_field: 'display_name', search: true } } })),
  fieldKind('photo', 'Photo (preuve)', 'string', () => ({ presentation: { widget: 'photo' }, evidence: { min_count: 1, kind: 'photo' } })),
  fieldKind('signature', 'Signature (preuve)', 'string', () => ({ presentation: { widget: 'signature' }, evidence: { min_count: 1, kind: 'signature' } })),
].map((kind) => ({
  ...kind,
  ...(KIND_ICONS[kind.id] === undefined ? {} : { icon: KIND_ICONS[kind.id] }),
  // « Non conforme » de la liste de valeurs vise le champ créé lui-même.
  create: (template: Template) => JSON.parse(JSON.stringify(kind.create(template)).replace(/__self__/g, freshId(template, 'champ'))) as unknown,
}));

export const SECTION_KINDS: readonly BlockKind<Template>[] = [
  { id: 'section', label: 'Section', glyph: '§', icon: 'rows', create: (template) => ({ section_id: freshId(template, 'section'), label: { fr: 'Nouvelle section' }, fields: [FIELD_KINDS[0]!.create(template) as Entry] }) },
  {
    id: 'series',
    label: 'Série ouverte',
    glyph: '⋯',
    icon: 'table',
    create: (template) => ({ section_id: freshId(template, 'serie'), label: { fr: 'Nouvelle série' }, series: { min_count: 1, max_count: 20 }, fields: [FIELD_KINDS[1]!.create(template) as Entry] }),
  },
];

function entryMarks(entry: Entry): string[] {
  const marks: string[] = [];
  if (entry.field.required === true) marks.push('obligatoire');
  else if (entry.required_when !== undefined) marks.push('obligatoire si…');
  if (entry.visible_when !== undefined) marks.push('visible si…');
  if (entry.target !== undefined) marks.push('cible');
  if (entry.evidence !== undefined) marks.push(entry.evidence.min_count === 0 ? 'preuve facultative' : 'preuve');
  if (entry.compute !== undefined) marks.push('calculé');
  return marks;
}

function entryNode(entry: Entry, list: Pointer, index: number, size: number, group: string): EditorNode<Template> {
  return {
    pointer: `${list}/${index}`,
    kind: 'field',
    label: text(entry.field.label, 'fr', entry.field.field_id),
    detail: (entry.presentation as { widget?: string } | undefined)?.widget === 'person' && entry.field.type === 'reference' ? 'Personne' : (TYPE_LABELS[entry.field.type] ?? entry.field.type),
    glyph: TYPE_GLYPHS[entry.field.type] ?? '?',
    position: { list, index, size, minimum: 1, group },
    marks: entryMarks(entry),
    children: [],
  };
}

/** Arbre des blocs du gabarit. */
export function templateTree(template: Template): EditorNode<Template> {
  const sections = template.sections.map((section, s): EditorNode<Template> => {
    const pointer = `/sections/${s}`;
    const fieldsList = `${pointer}/fields`;
    const grouped = section.repeat !== undefined || section.series !== undefined;
    const children = section.fields.map((entry, f) => entryNode(entry, fieldsList, f, section.fields.length, grouped ? `group:${s}` : 'field'));
    const childFields = section.repeat?.children?.fields;
    const childList = `${pointer}/repeat/children/fields`;
    if (childFields !== undefined) children.push(...childFields.map((entry, f) => entryNode(entry, childList, f, childFields.length, `children:${s}`)));
    const marks: string[] = [];
    if (section.visible_when !== undefined) marks.push('visible si…');
    return {
      pointer,
      kind: section.repeat !== undefined ? 'repeat' : section.series !== undefined ? 'series' : 'section',
      label: text(section.label, 'fr', section.section_id),
      detail: section.repeat !== undefined ? 'une par élément' : section.series !== undefined ? 'série ouverte' : `${section.fields.length} champ(s)`,
      glyph: section.repeat !== undefined ? '↻' : section.series !== undefined ? '⋯' : '§',
      position: { list: '/sections', index: s, size: template.sections.length, minimum: 1, group: 'section' },
      marks,
      insert: [
        { list: fieldsList, label: 'Ajouter un champ', kinds: FIELD_KINDS },
        ...(childFields === undefined ? [] : [{ list: childList, label: 'Ajouter un champ de sous-ligne', kinds: FIELD_KINDS }]),
      ],
      children,
    };
  });
  return { pointer: '', kind: 'template', label: text(template.label, 'fr', 'Formulaire'), glyph: '▤', insert: [{ list: '/sections', label: 'Ajouter une section', kinds: SECTION_KINDS }], children: sections };
}

/** Bloc qui porte un chemin (constat, erreur serveur) : le champ, sinon la section, sinon le formulaire. */
export function nodeOfPath(template: Template, path: string): Pointer {
  const segments = parsePointer(path === '/' ? '' : path);
  for (const length of [6, 4, 2]) {
    if (segments.length < length) continue;
    const prefix = formatPointer(segments.slice(0, length));
    const shape = segments.slice(0, length);
    const isEntry = (length === 4 && shape[0] === 'sections' && shape[2] === 'fields') || (length === 6 && shape[2] === 'repeat' && shape[3] === 'children' && shape[4] === 'fields');
    const isSection = length === 2 && shape[0] === 'sections';
    if ((isEntry || isSection) && getAt(template, prefix) !== undefined) return prefix;
  }
  return '';
}

/** Pointeur d'un bloc → sorte de bloc (formulaire, section, champ). */
export function blockKindAt(pointer: Pointer): 'template' | 'section' | 'field' | null {
  const segments = parsePointer(pointer);
  if (segments.length === 0) return 'template';
  if (segments.length === 2 && segments[0] === 'sections') return 'section';
  if ((segments.length === 4 && segments[2] === 'fields') || (segments.length === 6 && segments[2] === 'repeat' && segments[4] === 'fields')) return 'field';
  return null;
}

/** Variables d'une condition posée sur un bloc : les champs de son groupe de réponses (`$answers`). */
export function scopeOf(template: Template, pointer: Pointer): ScopeVariable[] {
  const segments = parsePointer(pointer);
  const variable = (entry: Entry): ScopeVariable => {
    const type = entry.field.type;
    const values = entry.field.validation?.enum_values ?? [];
    const labels = (entry.field.validation?.enum_labels ?? {}) as Record<string, Record<string, string>>;
    return {
      ref: `$answers.${entry.field.field_id}`,
      label: text(entry.field.label, 'fr', entry.field.field_id),
      type: type === 'enumeration' ? 'enum' : type === 'boolean' ? 'boolean' : ['number', 'decimal', 'quantity', 'money'].includes(type) ? 'number' : type === 'date' ? 'date' : type === 'string' ? 'string' : 'other',
      ...(type === 'enumeration' ? { options: values.map((value) => ({ value, label: text(labels[value], 'fr', value) })) } : {}),
    };
  };
  if (segments[0] === 'sections' && segments.length >= 2) {
    const section = template.sections[Number(segments[1])];
    if (section !== undefined && segments[2] === 'repeat') return (section.repeat?.children?.fields ?? []).map(variable);
    if (section !== undefined && (section.repeat !== undefined || section.series !== undefined) && segments.length >= 4) return section.fields.map(variable);
  }
  return template.sections.filter((section) => section.repeat === undefined && section.series === undefined).flatMap((section) => section.fields.map(variable));
}

/** Contrôles locaux immédiats : identifiants, libellés, valeurs d'énumération, type d'objet, bornes de série. */
export function localFindings(template: Template): Finding[] {
  const findings: Finding[] = [];
  const add = (severity: Finding['severity'], code: string, path: string, message: string): void => {
    findings.push({ severity, code, path, message });
  };
  if (text(template.label) === '') add('error', 'LABEL_MISSING', '/label', 'Le formulaire a un libellé en français.');
  if (template.sections.length === 0) add('error', 'SECTIONS_EMPTY', '/sections', 'Le formulaire a au moins une section.');
  const flat = new Map<string, string>();
  const checkEntry = (entry: Entry, path: string, scope: Map<string, string>): void => {
    const id = entry.field.field_id;
    if (!ID_PATTERN.test(id ?? '')) add('error', 'FIELD_ID_INVALID', `${path}/field/field_id`, `« ${id} » : minuscules, chiffres et « _ », commençant par une lettre.`);
    else if (scope.has(id)) add('error', 'DUPLICATE_FIELD_ID', `${path}/field/field_id`, `Le champ « ${id} » existe déjà (${scope.get(id)}).`);
    else scope.set(id, path);
    if (text(entry.field.label as Record<string, string> | undefined) === '') add('error', 'LABEL_MISSING', `${path}/field/label`, `Le champ « ${id} » a un libellé en français.`);
    if (entry.field.type === 'enumeration' && (entry.field.validation?.enum_values ?? []).length === 0) add('error', 'ENUM_VALUES_MISSING', `${path}/field/validation`, `La liste « ${id} » déclare au moins une valeur.`);
    if (entry.field.type === 'reference' && entry.field.reference_type === undefined) add('error', 'REFERENCE_TYPE_MISSING', `${path}/field`, `« ${id} » nomme le type d’objet à choisir.`);
    if (entry.target !== undefined && entry.target.rule_ref === undefined && entry.target.min === undefined && entry.target.max === undefined) add('error', 'TARGET_EMPTY', `${path}/target`, 'Une cible cite une règle de conformité ou porte une borne.');
    if (entry.presentation?.selector !== undefined && entry.field.type !== 'reference') add('warning', 'SELECTOR_NOT_REFERENCE', `${path}/presentation/selector`, 'Un sélecteur ne sert qu’à un champ « Objet à choisir ».');
  };
  const sectionIds = new Set<string>();
  template.sections.forEach((section, s) => {
    const path = `/sections/${s}`;
    if (!ID_PATTERN.test(section.section_id ?? '')) add('error', 'SECTION_ID_INVALID', `${path}/section_id`, `« ${section.section_id} » : minuscules, chiffres et « _ ».`);
    else if (sectionIds.has(section.section_id)) add('error', 'DUPLICATE_SECTION_ID', `${path}/section_id`, `La section « ${section.section_id} » existe déjà.`);
    sectionIds.add(section.section_id);
    if (text(section.label) === '') add('error', 'LABEL_MISSING', `${path}/label`, 'La section a un libellé en français.');
    if (section.fields.length === 0) add('error', 'FIELDS_EMPTY', `${path}/fields`, 'La section a au moins un champ.');
    const grouped = section.repeat !== undefined || section.series !== undefined;
    const scope = grouped ? new Map<string, string>() : flat;
    section.fields.forEach((entry, f) => checkEntry(entry, `${path}/fields/${f}`, scope));
    const children = new Map<string, string>();
    (section.repeat?.children?.fields ?? []).forEach((entry, f) => checkEntry(entry, `${path}/repeat/children/fields/${f}`, children));
    if (section.series !== undefined && section.series.min_count !== undefined && section.series.max_count !== undefined && section.series.min_count > section.series.max_count) add('error', 'SERIES_BOUNDS', `${path}/series`, 'Le minimum de lignes dépasse le maximum.');
  });
  return findings;
}

/**
 * Estimation du classement dans le navigateur : la même règle que le serveur (`classifyTemplateChange`,
 * ADR-141 décision 6 : un libellé seul passe sans seconde personne). Le serveur décide à la proposition.
 */
export function isLabelPath(path: string): boolean {
  return /\/(label|description|prompt)(\/[a-z]{2}(-[A-Za-z]{2})?)?$/.test(path) || /\/enum_labels\/[^/]+(\/[a-z]{2}(-[A-Za-z]{2})?)?$/.test(path);
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (isRecord(value)) return `{${Object.keys(value).sort().filter((key) => value[key] !== undefined).map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`;
  return JSON.stringify(value ?? null);
}

export function diffPointers(base: unknown, draft: unknown, path = ''): string[] {
  if (canonical(base) === canonical(draft)) return [];
  if (Array.isArray(base) && Array.isArray(draft) && base.length === draft.length) return base.flatMap((item, index) => diffPointers(item, draft[index], `${path}/${index}`));
  if (isRecord(base) && isRecord(draft)) {
    const keys = [...new Set([...Object.keys(base), ...Object.keys(draft)])].sort();
    return keys.flatMap((key) => diffPointers(base[key], draft[key], formatPointer([...parsePointer(path), key])));
  }
  return [path === '' ? '/' : path];
}

export function classifyTemplate(base: Template | null, draft: Template): ChangeClass {
  const changes = diffPointers(base, draft).map((path) => ({ path, kind: isLabelPath(path) ? ('label' as const) : ('structure' as const) }));
  return { structural: changes.some((change) => change.kind === 'structure'), changes };
}
